import { registerLocaleData } from "@angular/common";
import localeJa from "@angular/common/locales/ja";
import { Component, ChangeDetectionStrategy } from "@angular/core";
import { Device } from "@awesome-cordova-plugins/device/ngx";
import { SplashScreen } from "@awesome-cordova-plugins/splash-screen/ngx";
import { StatusBar } from "@awesome-cordova-plugins/status-bar/ngx";
import {
  IonApp,
  IonRouterOutlet,
  LoadingController,
  NavController,
  Platform,
} from "@ionic/angular/standalone";
import { Subscription } from "rxjs";
import { AppConfig } from "../app/app.config";
import { AnonymousDeviceTrackingProvider } from "./api/anonymous-device-tracking/anonymous-device-tracking.api";
import { Api } from "./api/base";
import { ChatRoomProvider } from "./api/chat-room/chat-room.api";
import { Events } from "./api/events/events";
import { MaintenanceProvider } from "./api/maintenance/maintenance";
import { UserProvider } from "./api/user/user.api";
import { ChatRoom } from "./models/chat-room";
import { FAEventTag } from "./const/fa-event-tag";
import { LocalStorageKey } from "./local-storage-key";
import { ApplicationError, AuthenticationFailedError } from "./models/errors";
import { SignUpDataOAuth } from "./models/signup-data";
import { Me } from "./models/user";
import { UserLine } from "./models/user-line";
import { OnboardingService } from "./pages/onboarding/onboarding.service";
import { AlertProvider } from "./services/alert/alert.service";
import { startImgixFallbackWatchdog } from "./services/imgix-fallback/imgix-fallback";
import { Auth } from "./services/auth/auth.service";
import { CouponCodePrefillService } from "./services/coupon-code-prefill/coupon-code-prefill.service";
import { AngularFireProvider } from "./services/firebase/angularfire.service";
import { ClientDiagnosticsService } from "./services/client-diagnostics/client-diagnostics.service";
import { StartupTraceService } from "./services/client-diagnostics/startup-trace.service";
import { LineProvider } from "./services/line/line.service";
import { NavParamsService } from "./services/nav-params/nav-params.service";
import { TrackerService } from "./services/tracker/tracker.service";
import { Util } from "./services/util/util";
import { restoreWebTrackTokensFromCookie } from "./services/util/web-track-token.util";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonRouterOutlet, IonApp],
})
export class AppComponent {
  // @ViewChild(Nav) nav: Nav;
  me: Me;
  isMaintenance = false;
  /** 複数回subscribeされるのを防ぐ */
  resumeEmitter: Subscription;
  /** 複数回subscribeされるのを防ぐ */
  pauseEmitter: Subscription;
  /** 一定時間内に複数回のresumeを発火しないように制御する */
  lastResumeEpochTime: number;
  /** 一定時間内に複数回のpauseを発火しないように制御する */
  lastPauseEpochTime: number;
  /** FIXME: resumeが無限ループするので、その対処。原因は不明 */
  isShowingErrorAlert = false;
  isInitialized = false;
  url: string;
  uuid: string;
  /** メンテナンスチェックと並行して先行実行する認証準備(/me取得)のPromise */
  private authPrefetch?: Promise<Me | false>;

  constructor(
    private tracker: TrackerService,
    private auth: Auth,
    private api: Api,
    private afp: AngularFireProvider,
    private navCtrl: NavController,
    private navParams: NavParamsService,
    private maintenanceProvider: MaintenanceProvider,
    private alertCtrl: AlertProvider,
    private events: Events,
    private platform: Platform,
    private userProvider: UserProvider,
    private statusBar: StatusBar,
    private device: Device,
    private splashScreen: SplashScreen,
    private lineProvider: LineProvider,
    private onboardingService: OnboardingService,
    private chatRoomProvider: ChatRoomProvider,
    private loadingCtrl: LoadingController,
    private couponCodePrefillService: CouponCodePrefillService,
    private anonymousDeviceTrackingProvider: AnonymousDeviceTrackingProvider,
    private clientDiagnostics: ClientDiagnosticsService,
    private startupTrace: StartupTraceService
  ) {
    // 起動が最初の画面の表示まで届かなかった場合に、どこで止まったかを調査できるよう節目を記録する(エスカ#1303)
    this.startupTrace.begin();
    registerLocaleData(localeJa);

    // swiperのカスタム要素登録は遅延ロードする(静的importだと初期バンドルに約55KB gzipが乗るため)。
    // 利用側コンポーネントはcustomElements.whenDefined("swiper-container")で登録完了を待つ
    import("swiper/element/bundle")
      .then((m) => m.register())
      .catch((e) => console.log(e));

    // imgix障害時に画像を元URL(Firebase Storage)へ自動フォールバックさせる監視を開始
    startImgixFallbackWatchdog();
    // 白画面などの表示失敗を調査できるよう、直近の操作の記録と空モーダルの検出を開始
    this.clientDiagnostics.start();

    this.url = window.document.URL;
    this.platform.ready().then(() => {
      this.startupTrace.mark("platform_ready");
      this.statusBar.styleDefault();
      this.statusBar.overlaysWebView(false);
      this.statusBar.backgroundColorByHexString("ffffff");
      // TODO: ionic6対応。2023/05/06に対応済み
      if (this.device.platform && !AppConfig.IS_BROWSER) {
        this.uuid = this.device.uuid;
      }
      this.tracker.init();
      this.initialize();
    });
  }

  /** 初期化処理 */
  initialize(): void {
    // アプリ起動時の最初期に1回実行: CookieからLocalStorageへWebトラックトークンを復元
    restoreWebTrackTokensFromCookie();
    // アプリ起動時の最初期に1回実行: 合言葉クーポンのディープリンクパラメータ(ct=署名トークン。旧形式は cc)を退避
    // (LINEログイン等のOAuthリダイレクトでdeepLinksが上書きされる前に退避する)
    this.couponCodePrefillService.captureFromUrl();

    this.isMaintenance = false;
    this.setPatolarClass();
    this.setDesktopClass();
    if (!AppConfig.IS_BROWSER) {
      // アプリでしか動かないため
      this.setResumeSubscriber();
      this.setPauseSubscriber();
    }
    // メンテナンスチェックと認証準備(/me取得)を並行実行し、起動の直列ネットワーク待ちを削減する。
    // 認証結果の適用はメンテナンスチェック成功後のauthenticate()で行う
    this.startAuthPrefetch();
    this.checkMaintenanceSubscriber();
  }

  /**
   * メンテナンスチェックと並行して認証準備を開始する。
   * ヘッダー初期化・storage初期化・/me取得までを先行実行し、
   * ログイン副作用の適用(applyLogin)はメンテナンスチェック成功後まで行わない。
   */
  private startAuthPrefetch(): void {
    const url = localStorage.getItem(LocalStorageKey.User.DeepLinks) ?? "";
    const liffId = Util().getQueryVariable(url, "liff_id");
    if (this.lineProvider.isPatoPassportLiffId(liffId)) {
      // PatoPassport LIFF起動時はauthenticate()が専用のログイン処理に入るため先行取得しない
      return;
    }
    this.authPrefetch = this.prefetchAuth();
    // メンテナンス中(599)は消費されないため、未処理のPromise rejectionにしない
    this.authPrefetch.catch(() => {});
  }

  private async prefetchAuth(): Promise<Me | false> {
    await this.api.initializeHeader(
      this.platform.is("desktop"),
      this.platform.is("ios")
    );
    this.startupTrace.headerReady();
    this.afp.setPrefix(AppConfig.FIREBASE_PREFIX);
    await this.auth.initStorage();
    this.startupTrace.mark("storage_ready");
    // 認証応答を待つ間ネットワークが空くため、初回遷移先のJSチャンクを先行ロードしておく
    const token = await this.auth.getToken();
    this.preloadFirstRouteChunk(!!token);
    const me = await this.auth.fetchMe();
    this.startupTrace.mark(me ? "me_ok" : "me_none");
    if (me) {
      this.preloadInitialTabChunk(me);
    } else {
      // 未ログインの場合のみ記録する。ログイン済みの再訪はupdateWebTrackToken側で記録される
      this.reportAnonymousDeviceTrackingIfNeeded();
    }
    return me;
  }

  /**
   * 未ログイン状態でweb_track_token付きリンクからアプリを起動したことを記録する。
   * アカウント作成に至らなかったダウンロードを捕捉するための非認証API呼び出しで、
   * 失敗しても起動フローに影響させないためfire-and-forgetにする(dedupはサーバ側で行う)
   */
  private reportAnonymousDeviceTrackingIfNeeded(): void {
    const webTrackToken = this.onboardingService.getWebTrackToken();
    if (!webTrackToken) {
      return;
    }
    this.anonymousDeviceTrackingProvider.create(webTrackToken).subscribe({
      error: (e) => console.warn("anonymous device tracking failed:", e),
    });
  }

  /**
   * 初回遷移先チャンクの先行ロード。
   * 失敗しても通常のlazy loadで再取得されるため握りつぶしてよい。
   * import()のパスはapp-routing.ts/tabs.router.tsと同一specifierのため同じチャンクに解決される
   */
  private preloadFirstRouteChunk(hasToken: boolean): void {
    if (hasToken) {
      import("./pages/tabs/tabs.router").catch(() => {});
    } else if (
      this.checkCastRelatedUrl(
        localStorage.getItem(LocalStorageKey.User.DeepLinks) || document.URL
      )
    ) {
      import(
        "./pages/onboarding/onboarding-for-cast/onboarding-for-cast.page"
      ).catch(() => {});
    } else {
      import("./pages/onboarding/onboarding-for-guest/onboarding.page").catch(
        () => {}
      );
    }
  }

  /** tabsシェル描画後に選択される初期タブ(tabs.tsのタブ選択ロジックと対応)のチャンクを先行ロードする */
  private preloadInitialTabChunk(me: Me): void {
    if (me.isCast()) {
      if (me.isRecess()) {
        import("./pages/chat-rooms/chat-rooms").catch(() => {});
      } else {
        import("./pages/home/cast-home/cast-home").catch(() => {});
      }
    } else {
      import("./pages/search-tab/search-tab.page").catch(() => {});
    }
  }

  /** app用、cssを設定する */
  setPatolarClass() {
    if (Util().isPatolar() || Util().isPatoBrowser()) {
      document.documentElement.classList.add("patolar-nav");
    }
  }

  /** Desktop用、cssを設定する */
  setDesktopClass() {
    if (this.platform.is("desktop")) {
      document.documentElement.classList.add("pc-nav");
    }
  }

  /** 姉pato設定 */
  setRichPatoCss() {
    if (this.me && this.me.isGuest() && this.me.isRichPato()) {
      document.documentElement.classList.add("rich-pato-nav");
    }
  }

  /** patolar用、cssを設定する */
  setTabletClass() {
    document.documentElement.classList.add("pato-tablet-nav");
  }

  /** platformのresume処理を設定する */
  setResumeSubscriber() {
    if (!this.resumeEmitter) {
      this.resumeEmitter = this.platform.resume.subscribe(() => {
        this.checkResume();
      });
    }
  }

  checkResume() {
    const currentEpochTime = new Date().getTime();
    // 5秒以内のresumeは無視する
    if (
      this.lastResumeEpochTime &&
      this.lastResumeEpochTime + 5000 > currentEpochTime
    ) {
      return;
    }
    this.lastResumeEpochTime = currentEpochTime;
    this.startupTrace.mark("resume");
    this.maintenanceProvider.check().subscribe((res) => {
      // 再起動チェック
      if (res && res.force_reload) {
        this.maintenanceProvider.restartWithCheck(res.force_reload);
      }
      if (this.isMaintenance) {
        this.initialize();
      } else {
        this.events.publish("platform:resume");
      }
    }, this.mantenanceHandler.bind(this));

    // 起動時ログ
    this.tracker.trackForFirebaseAnalytics(FAEventTag.Load.Ressume, {
      is_resume: 1, // 1はis_resumeオン
      uuid: this.uuid,
    });
  }

  /** platformのpause処理を設定する */
  setPauseSubscriber() {
    if (!this.pauseEmitter) {
      this.pauseEmitter = this.platform.pause.subscribe(() => {
        const currentEpochTime = new Date().getTime();
        // 5秒以内のresumeは無視する
        if (
          this.lastPauseEpochTime &&
          this.lastPauseEpochTime + 5000 > currentEpochTime
        ) {
          return;
        }
        this.lastPauseEpochTime = currentEpochTime;
        this.events.publish("platform:pause");
      });
    }
  }

  /** メンテナンス処理のSubscriberを設定 */
  checkMaintenanceSubscriber() {
    this.maintenanceProvider.check().subscribe((res) => {
      this.startupTrace.mark("maintenance_ok");
      if (!this.isInitialized) {
        if (res && res.force_reload) {
          this.maintenanceProvider.setLastForceReloadTimestamp(
            res.force_reload
          );
        }
        this.isInitialized = true;
        // ヘッダー初期化はstartAuthPrefetch()側で並行実行済み
        this.authenticate();
      }
    }, this.mantenanceHandler.bind(this));
  }

  mantenanceHandler(error): void {
    this.startupTrace.mark(`maintenance_error:${error?.status ?? ""}`);
    if (error.status && error.status === 599) {
      this.isMaintenance = true;
      this.splashScreen.hide();
      this.navCtrl.navigateRoot("MaintenancePage");
    } else {
      if (!this.isShowingErrorAlert) {
        this.isShowingErrorAlert = true;

        this.alertCtrl.create({
          header: "エラー",
          subHeader:
            "エラーが発生しました。リトライまたは時間をおいてから再起動をお試しください。",
          buttons: [
            {
              text: "リトライする",
              handler: () => {
                this.isShowingErrorAlert = false;
                this.checkMaintenanceSubscriber();
              },
            },
          ],
        });
      }
    }
  }

  async successAuthenticate() {
    this.me = this.auth.getMe();
    this.startupTrace.setUserId(this.me.id);

    // business-hubから別ユーザーのlogin_token付きで来た場合、アカウント切替確認
    const switched = await this.confirmSwitchAccountByLoginToken();
    if (switched) {
      // 切り替えた場合、successAuthenticateを最初からやり直す
      this.me = this.auth.getMe();
    }

    // 友だち追加先OA(LINEシャーディングで可変)を先読みする。connectLine内で待たないため
    this.lineProvider.prefetchAddFriendTarget();

    // pato VIPキャストLINEアカウント同期処理
    if (Util().isLiffClient() && this.lineProvider.isPatoVIPCastLineApp()) {
      try {
        const lineProfile: UserLine =
          await this.lineProvider.initializeForLiffWithProfile();
        this.userProvider.lineLinkedLineBotUser(
          lineProfile.userId,
          this.lineProvider.getLiffId()
        );
      } catch (e) {
        console.log(e);
      }
    }

    const loginToken: string = Util().getQueryVariable(
      document.URL,
      "in_app_line_logined_user_id"
    );
    if (loginToken) {
      const url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
      const callbackTokenCode = Util().getQueryVariable(url, "code");

      const lineLoginType =
        localStorage.getItem(LocalStorageKey.User.LINELoginType) || null;
      localStorage.removeItem(LocalStorageKey.User.LINELoginType);
      // 新規登録用のシャードOAチャネルでログインした場合は、そのチャネルIDをサーバへ送る
      // (コールバックURLにstateが残っていれば照合し、別ログインの古い値を載せない)
      const callbackTokenState =
        Util().getQueryVariable(url, "state") || undefined;
      const lineLoginChannelId =
        this.lineProvider.consumeLoginChannelId(callbackTokenState);

      const signUpData: SignUpDataOAuth = new SignUpDataOAuth({
        app_id: AppConfig.META_DATA.appID,
        os_type: this.maintenanceProvider.getOStype(),
        code: callbackTokenCode,
        line_login_type: lineLoginType,
        line_login_channel_id: lineLoginChannelId,
      });

      this.auth.requestNeedParamsForLineLogin(signUpData);
    }

    // 起動時ログ
    this.sendLaunchLog();
    this.setRichPatoCss();
    this.splashScreen.hide();
    this.startupTrace.mark("splash_hide");

    // タブレットユーザーの場合のみ専用ページに遷移する
    this.setRootPage();
  }

  setRootPage() {
    // partner_hub への deeplink リダイレクトはユーザー種別に依らず最優先で処理する
    const deeplinkUrl =
      localStorage.getItem(LocalStorageKey.User.DeepLinks) || document.URL;
    if (deeplinkUrl.indexOf("PartnerHubRedirectPage") !== -1) {
      // クエリパラメータ (e.g. ?dest=...) を保持したまま遷移する
      const path = deeplinkUrl.replace(/^.*#\//, "");
      this.navCtrl.navigateRoot(path);
      return;
    }

    // タブレットユーザーの場合のみ専用ページに遷移する
    if (this.me.isGuest() && this.me.isTabletUserType()) {
      this.setTabletClass();
      this.changeViewPortForTableHome();
      this.subscribeUserForceReload();
      this.navCtrl.navigateRoot("TabletHomePage");
    } else if (
      this.me.isGuest() &&
      this.me.isShopOwnerUserType() &&
      !this.me.isOrganizationUserDeleted()
    ) {
      // 店舗ユーザーは身分証明をスキップする方針のため、ReadyUsedPage は表示しない。
      // クレジットカード未登録の場合は business-hub の /under-review 側で
      // CreditCardsPage への deeplink を案内する。
      const url =
        localStorage.getItem(LocalStorageKey.User.DeepLinks) || document.URL;
      // urlのパラメータによって画面遷移
      if (url.indexOf("CastRequestByShopOwnerPage") !== -1) {
        // 旧発注フォームへの deeplink は business-hub の発注フォームへリダイレクトする
        this.navCtrl.navigateRoot("PartnerHubRedirectPage", {
          queryParams: { dest: "orders-new" },
        });
      } else if (url.indexOf("TodayEntryCastListPage") !== -1) {
        // 旧本日出勤キャスト一覧への deeplink は business-hub 側の同等画面へリダイレクトする
        this.navCtrl.navigateRoot("PartnerHubRedirectPage", {
          queryParams: { dest: "today-entry" },
        });
      } else if (url.indexOf("CastRequestSelectChoiceCallPage") !== -1) {
        // business-hub からの deeplink 遷移
        const path = url.replace(/^.*#\//, "");
        this.navCtrl.navigateRoot(path);
      } else if (url.indexOf("CastRequestDetailPage") !== -1) {
        // business-hub からの deeplink 遷移
        const path = url.replace(/^.*#\//, "");
        this.navCtrl.navigateRoot(path);
      } else if (url.indexOf("CreditCardsPage") !== -1) {
        // business-hub のメニュー「支払い情報登録」からの deeplink 遷移
        const path = url.replace(/^.*#\//, "");
        this.navCtrl.navigateRoot(path);
      } else if (url.indexOf("ConciergePage") !== -1) {
        // business-hub の /under-review からの deeplink 遷移
        // ConciergePage は実体ページではなくコンシェルジュチャットルームを開く処理
        // shop_owner_user は tabs に入らないため、ここで直接処理する
        this.navCtrl.navigateRoot("CastRequestListPage").then(async () => {
          const loading = await this.loadingCtrl.create();
          loading.present();
          this.chatRoomProvider
            .showOrCreateConciergeChatRoom(this.me.getId())
            .subscribe({
              next: async (room: ChatRoom) => {
                await loading.dismiss();
                this.navParams.set({ room: room });
                this.navCtrl.navigateForward([
                  "ChatPage",
                  new Date().getTime(),
                ]);
              },
              error: async () => {
                await loading.dismiss();
              },
            });
        });
      } else {
        // deeplink 指定がない通常起動時は business-hub (新UI) に遷移する
        // 旧UI(CastRequestListPage)に着地すると旧料金(5,000円〜)で注文できてしまうため
        this.navCtrl.navigateRoot("PartnerHubRedirectPage");
      }
    } else if (this.me.isGuest() && this.me.isSalonDisplayOnlyUserType()) {
      this.setTabletClass();
      this.subscribeUserForceReload();
      this.navCtrl.navigateRoot("SearchTabPage");
    } else if (this.me.isInreview() && this.me.isCast()) {
      if (Util().isLiffClient() || Util().isPatoForCast()) {
        this.onboardingService.handleCastInreviewNavigation(this.me);
      } else {
        this.checkLineInreviewStatus();
      }
    } else {
      // NOTE: iOS版patoの場合、利用準備が整ってなければ利用準備画面を表示する
      if (
        !this.me.hasUsedReady() &&
        this.me.isGuest() &&
        this.platform.is("ios") &&
        this.platform.is("capacitor") &&
        AppConfig.META_DATA.appID === "pato" &&
        !AppConfig.IS_BROWSER
      ) {
        this.navParams.set({
          firstLaunch: true,
        });
        this.navCtrl.navigateRoot("ReadyUsedPage");
      } else {
        this.navCtrl.navigateRoot("TabsPage");
      }
    }
  }

  /** LINE連携かつInreviewステータスの場合、LINE友達追加を表示する */
  checkLineInreviewStatus() {
    this.navCtrl.navigateRoot("CastLineAppliedPage");
  }

  /** ログイン認証用トークンがない場合 */
  async failAuthenticate(): Promise<void> {
    this.startupTrace.mark("auth_fail");
    this.splashScreen.hide();
    const loginToken: string = this.getLoginToken();
    let res: boolean;
    if (loginToken !== "super-organizer") {
      res = await this.checkLoginToken(loginToken);
    }
    if (
      loginToken !== "super-organizer" &&
      !res &&
      this.getPatolarLoginToken()
    ) {
      res = await this.checkLoginToken(this.getPatolarLoginToken());
      localStorage.setItem(LocalStorageKey.Guest.IsPatolarLogin, "true");
    }
    if (!res && Util().isLiffClient() && this.lineProvider.isPatoApp()) {
      // liff login
      try {
        const lineProfile: UserLine =
          await this.lineProvider.initializeForLiffWithProfile();
        res = await this.loginLiff(lineProfile);
      } catch (e) {
        console.log(e);
      }
    }
    if (res) {
      this.me = this.auth.getMe();
      // タブレットユーザーの場合のみ専用ページに遷移する
      this.setRootPage();
    } else if (loginToken === "super-organizer") {
      // NOTE: ここでオーガナイザートークンがあれば、スーパーオーガナイザーページを表示する。
      this.navCtrl.navigateRoot("InvitationLoginPage");
    } else {
      this.navigateToOnboardingPage();
    }

    // 起動時ログ
    this.sendLaunchLog();
  }

  /**
   * オンボーディングページへの遷移を決定する
   * URLパラメータやDeepLinksの内容に基づいて適切なページに遷移
   */
  private navigateToOnboardingPage(): void {
    const url =
      localStorage.getItem(LocalStorageKey.User.DeepLinks) || document.URL;

    // キャスト応募関連のパラメータをチェック
    const isCastRelated = this.checkCastRelatedUrl(url);

    if (isCastRelated) {
      // キャスト向けオンボーディングページへ遷移
      localStorage.setItem(LocalStorageKey.User.ForCastLogin, "true");
      this.navCtrl.navigateRoot("OnboardingForCastPage");
    } else {
      // 通常のオンボーディングページへ遷移
      this.navCtrl.navigateRoot("OnboardingPage");
    }
  }

  /**
   * URLがキャスト関連かどうかを判定する
   * @param url チェックするURL
   * @returns キャスト関連のURLかどうか
   */
  private checkCastRelatedUrl(url: string): boolean {
    if (
      this.platform.is("ios") &&
      !AppConfig.IS_BROWSER &&
      AppConfig.META_DATA.appID === "pato"
    ) {
      return true;
    }
    if (!url) {
      // localStorageもチェック
      const forCast = localStorage.getItem(LocalStorageKey.User.ForCastLogin);
      return forCast === "true";
    }

    // URLパラメータもチェック
    const urlParams = ["for_cast"];

    const hasCastParam = urlParams.some((param) =>
      url.toLowerCase().includes(param.toLowerCase())
    );

    // localStorageもチェック
    const forCast = localStorage.getItem(LocalStorageKey.User.ForCastLogin);
    return hasCastParam || forCast === "true";
  }

  /** ログイントークン取得処理 */
  getLoginToken(): string {
    let loginToken: string = Util().getQueryVariable(
      document.URL,
      "login_token"
    );
    // LINEから来たとき用のlogin_token取得処理
    if (
      !loginToken &&
      Util().getQueryVariable(document.URL, "re_login_token")
    ) {
      loginToken = Util().getQueryVariable(document.URL, "re_login_token");
    }
    // スーパーアドバイザー用のlogin_token取得処理
    if (!loginToken && Util().getQueryVariable(document.URL, "invitation")) {
      loginToken = Util().getQueryVariable(document.URL, "invitation");
    }
    return loginToken;
  }

  /** patolarのログイントークン */
  getPatolarLoginToken(): string {
    const patolarLoginToken: string = Util().getQueryVariable(
      document.URL,
      "patolar_login_token"
    );
    return patolarLoginToken;
  }

  /**
   * business-hubから別ユーザーのlogin_token付きで来た場合に、
   * アカウント切替の確認ダイアログを出す。
   * 切り替えた場合は true を返す。
   */
  async confirmSwitchAccountByLoginToken(): Promise<boolean> {
    const loginToken = Util().getQueryVariable(document.URL, "login_token");
    if (!loginToken) return false;

    const tokenUserId = await this.auth.peekLoginTokenUserId(loginToken);
    if (!tokenUserId) return false;

    // 同じユーザーなら切替不要
    if (tokenUserId === this.me.id) return false;

    const confirmed = await new Promise<boolean>((resolve) => {
      this.alertCtrl.create({
        header: "アカウントを切り替えますか？",
        message:
          "別のアカウントでログインしています。アカウントを変更しますか？",
        buttons: [
          { text: "いいえ", role: "cancel", handler: () => resolve(false) },
          { text: "はい", handler: () => resolve(true) },
        ],
      });
    });

    if (!confirmed) return false;

    await this.checkLoginToken(loginToken);
    return true;
  }

  /** ログイントークンを使った、ログイン処理 */
  async checkLoginToken(loginToken: string): Promise<boolean> {
    if (loginToken) {
      try {
        await this.auth.tryLoginByLoginToken(loginToken);
        return true;
      } catch (error) {
        this.alertCtrl.basicAlert(
          "patoのログインに失敗しました",
          error.error.error_message
        );
        return false;
      }
    }
    return false;
  }

  /** Liffでのログイン処理 */
  async loginLiff(profile: UserLine): Promise<boolean> {
    if (profile) {
      try {
        await this.auth.tryLoginByLiff(
          profile.userId,
          profile.displayName,
          profile.pictureUrl,
          this.lineProvider.getLiffIdTokenForLogin()
        );
        return true;
      } catch (error) {
        this.alertCtrl.basicAlert(
          "pato(liff)のログインに失敗しました",
          error.error.error_message
        );
        return false;
      }
    }
    return false;
  }

  /** Liffでのログイン処理 */
  async loginLiffPatoPassport(profile: UserLine): Promise<boolean> {
    if (profile) {
      try {
        await this.auth.tryLoginByLiffPatoPassport(
          profile.userId,
          profile.displayName,
          profile.pictureUrl,
          this.lineProvider.getLiffIdTokenForLogin()
        );
        return true;
      } catch (error) {
        this.alertCtrl.basicAlert(
          "patoのログインに失敗しました",
          error.error.error_message
        );
        return false;
      }
    }
    return false;
  }

  async loginPatoPassportLiff(liffId) {
    try {
      const lineProfile: UserLine =
        await this.lineProvider.initializeForLiffId(liffId);
      await this.loginLiffPatoPassport(lineProfile);
    } catch (e) {
      console.log(e);
    }
    this.navParams.set({
      liffId: liffId,
    });

    this.me = this.auth.getMe();
    this.onboardingService.handleCastInreviewNavigation(this.me);
  }

  /** 認証処理 */
  async authenticate() {
    const url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
    const liffId = Util().getQueryVariable(url, "liff_id");
    if (this.lineProvider.isPatoPassportLiffId(liffId)) {
      // PatoPassport LIFF起動時はprefetchを開始していないため、ここで初期化する
      await this.api.initializeHeader(
        this.platform.is("desktop"),
        this.platform.is("ios")
      );
      this.startupTrace.headerReady();
      this.afp.setPrefix(AppConfig.FIREBASE_PREFIX);
      await this.auth.initStorage();
      this.loginPatoPassportLiff(liffId);
      return;
    }

    // メンテナンスチェックと並行して先行取得した/me結果を消費する。
    // 認証エラー後のリトライ時などprefetchが無い場合はここで取得する
    const pendingMe = this.authPrefetch ?? this.fetchMeWithoutPrefetch();
    this.authPrefetch = undefined;
    pendingMe
      .then((me) => {
        if (me) {
          // ログイン副作用(トラッカー更新・未読数取得・イベント購読)はメンテナンスチェック成功後に適用する
          this.auth.applyLogin(me);
          this.successAuthenticate();
        } else {
          this.failAuthenticate();
        }
      })
      .catch((error) => {
        this.startupTrace.mark(`auth_error:${error?.name ?? "unknown"}`);
        if (error instanceof AuthenticationFailedError) {
          this.alertCtrl.create({
            header: error.name,
            subHeader: error.message,
            buttons: [
              {
                text: "OK",
                handler: () => {
                  Promise.all([
                    this.auth.removeToken(),
                    this.auth.removeUuid(),
                  ]).then(() => {
                    this.failAuthenticate();
                  });
                },
              },
            ],
          });
        } else if (error instanceof ApplicationError) {
          this.alertCtrl.create({
            header: error.name,
            subHeader: error.message,
            buttons: [
              {
                text: "OK",
                handler: () => {
                  this.authenticate();
                },
              },
            ],
          });
        } else {
          throw error;
        }
      });
  }

  /** prefetchを経由しない/me取得(認証エラー後のリトライ時に使用) */
  private async fetchMeWithoutPrefetch(): Promise<Me | false> {
    await this.auth.initStorage();
    return this.auth.fetchMe();
  }

  /** タブレット用のviewportへ動的に書き換える処理 */
  private changeViewPortForTableHome() {
    document
      .querySelector("meta[name='viewport']")
      .setAttribute(
        "content",
        "width=2000,user-scalable=no, maximum-scale=1.0"
      );
  }

  /** ユーザー別強制再起動処理 */
  private subscribeUserForceReload() {
    try {
      setInterval(() => {
        this.maintenanceProvider.checkUserForceReload().subscribe((res) => {
          if (res && res.user_force_reload) {
            this.maintenanceProvider.userForceRestartWithCheck(
              res.user_force_reload
            );
          }
        });
      }, 15000);
    } catch (e) {
      console.log(e);
    }
  }

  private sendLaunchLog() {
    try {
      let url = localStorage.getItem(LocalStorageKey.User.DeepLinks);
      let richMenu = ""; // LIFF以外から入ってきた場合は空文字列を渡すように変数宣言
      let isLiff = false;
      if (Util().getQueryVariable(url, "liff.state") && Util().isLiffClient()) {
        // LIFFで入ってきた場合のみURLのhashからrich_menuのデータを取得
        richMenu = Util().getUrlHash(url);
        // LIFFで入ってきた場合には、hashの値を載せ替える
        url =
          AppConfig.META_DATA.hostURL +
          decodeURIComponent(Util().getQueryVariable(url, "liff.state"));
        url = url.replace(/#\//, "");
        isLiff = true;
      }
      const reg = new RegExp("/designate-profile/([0-9a-zA-Z]+)");
      const match = url.match(reg);
      let userId = "";
      if (match && match.length >= 1 && match[1]) {
        userId = match[1];
      }

      this.tracker.trackForFirebaseAnalytics(FAEventTag.Load.Launch, {
        is_resume: 0,
        uuid: this.uuid,
        track_code: Util().getQueryVariable(url, "track_code"),
        from_line: Util().getQueryVariable(url, "from_line"),
        user_code: userId,
        rich_menu: richMenu.replace(/#\//, ""),
        is_liff: isLiff,
      });
    } catch (e) {
      console.log(e);
    }
  }
}
